import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  type OnDestroy,
  type OnInit,
  inject,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { PlatformAuthService } from '../../../../common/services/platform-auth/platform-auth.service';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface NavItem {
  title: string;
  icon?: string; // Optional, but required for level 1
  route?: string; // Mutually exclusive with children
  children?: NavItem[];
  isExpanded?: boolean; // State for items with children
  externalLink?: boolean; // Flag for external links
  isActive?: boolean;
}

export type SidebarState = 'collapsed' | 'expanded' | 'peeking';

const SIDEBAR_EXPANDED_STORAGE_KEY = 'spog-admin-sidebar-expanded';
const PEEK_HOVER_LEAVE_GRACE_MS = 200;

@Component({
  selector: 'app-sidebar-navigation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CdwIconModule,
    MatTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './sidebar-navigation.component.html',
  styleUrls: ['./sidebar-navigation.component.scss'],
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
  sidebarState: SidebarState = 'collapsed';

  @HostBinding('class.is-collapsed') get isCollapsedHost(): boolean {
    return this.sidebarState === 'collapsed';
  }
  @HostBinding('class.is-expanded') get isExpandedHost(): boolean {
    return this.sidebarState === 'expanded';
  }
  @HostBinding('class.is-peeking') get isPeekingHost(): boolean {
    return this.sidebarState === 'peeking';
  }

  @Input() userInfo: {
    name: string;
    avatarUrl: string;
    email?: string | null;
  } | null = {
    name: 'Admin User',
    avatarUrl: 'images/gopher.svg',
  };

  navigationItems: NavItem[] = [
    { title: 'Dashboard', icon: 'pie_chart', route: '/dashboard' },
    { title: 'Customers', icon: 'people', route: '/customers' },
    {
      title: 'License Lookup',
      icon: 'workspace_premium',
      route: '/licensing',
    },
    {
      title: 'QA Overrides',
      icon: 'science',
      route: '/qa-overrides',
    },
    {
      title: 'Platform Admins',
      icon: 'admin_panel_settings',
      route: '/admins',
    },
  ];

  private router = inject(Router);
  private readonly platformAuthService = inject(PlatformAuthService);
  private activeUrl = '';
  private routerSubscription!: Subscription;
  private peekCloseTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (this.readExpandedFromStorage()) {
      this.sidebarState = 'expanded';
    }

    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.activeUrl = event.urlAfterRedirects;
        this.updateActiveState();
        // Navigation is the only event that should auto-mutate which parent
        // is expanded. State transitions (toggle, peek, pin) preserve the
        // user's explicit choice.
        this.syncExpandedToActiveRoute();
      });

    this.activeUrl = this.router.url;
    this.updateActiveState();
    this.syncExpandedToActiveRoute();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.clearPeekCloseTimer();
  }

  /**
   * Sign out the current platform admin (clears the session + Firebase auth)
   * and return to the welcome screen.
   */
  logout(): void {
    this.platformAuthService.logout().subscribe(() => {
      this.router.navigate(['/welcome']);
    });
  }

  /**
   * Returns the icon to render in the arrow button for the current state.
   */
  get arrowIcon(): string {
    return this.sidebarState === 'collapsed'
      ? 'left_panel_open'
      : 'left_panel_close';
  }

  /**
   * Arrow click behavior:
   * - collapsed → expanded (persisted)
   * - expanded → collapsed (persistence cleared)
   * - peeking  → expanded (pin the peek; persisted)
   */
  onArrowClick(): void {
    this.clearPeekCloseTimer();
    if (this.sidebarState === 'expanded') {
      this.writeExpandedToStorage(false);
      this.setState('collapsed');
    } else {
      this.writeExpandedToStorage(true);
      this.setState('expanded');
    }
  }

  /**
   * Click on an expandable parent (item with children):
   * - collapsed: enter PEEK with this parent expanded
   * - expanded/peeking: just toggle this parent's children (mutual exclusion)
   */
  toggleItemExpansion(item: NavItem): void {
    if (!item.children) {
      return;
    }

    if (this.sidebarState === 'collapsed') {
      this.collapseAllTopLevelItems(item);
      item.isExpanded = true;
      this.setState('peeking');
      return;
    }

    const wasExpanded = item.isExpanded;
    this.collapseAllTopLevelItems(item);
    item.isExpanded = !wasExpanded;
  }

  /**
   * Click on a leaf navigation item: dismisses the peek if currently peeking,
   * leaves the persistent expanded state alone otherwise.
   */
  onLeafItemClick(): void {
    if (this.sidebarState === 'peeking') {
      this.clearPeekCloseTimer();
      this.setState('collapsed');
    }
  }

  onMouseEnter(): void {
    // Cancel any pending peek-close timer if the user re-enters the sidebar
    // before the grace period elapses.
    this.clearPeekCloseTimer();
  }

  onMouseLeave(): void {
    if (this.sidebarState !== 'peeking') {
      return;
    }
    this.clearPeekCloseTimer();
    this.peekCloseTimer = setTimeout(() => {
      this.peekCloseTimer = null;
      if (this.sidebarState === 'peeking') {
        this.setState('collapsed');
      }
    }, PEEK_HOVER_LEAVE_GRACE_MS);
  }

  private setState(next: SidebarState): void {
    if (this.sidebarState === next) {
      return;
    }
    this.sidebarState = next;
    if (next === 'collapsed') {
      this.collapseAllItems(this.navigationItems);
    }
    this.updateActiveState();
  }

  private clearPeekCloseTimer(): void {
    if (this.peekCloseTimer !== null) {
      clearTimeout(this.peekCloseTimer);
      this.peekCloseTimer = null;
    }
  }

  private readExpandedFromStorage(): boolean {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private writeExpandedToStorage(expanded: boolean): void {
    try {
      if (expanded) {
        localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(SIDEBAR_EXPANDED_STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable (private browsing, quota); silently ignore.
    }
  }

  /**
   * Walk navigationItems and return the path (array of items from top-level
   * to leaf) that best matches the current activeUrl. Empty array if none.
   */
  private findActiveBestMatch(): NavItem[] {
    let bestMatch: NavItem[] = [];

    const findPath = (
      items: NavItem[],
      currentPath: NavItem[],
      url: string
    ): void => {
      for (const item of items) {
        const newPath = [...currentPath, item];
        if (item.route && !item.externalLink && url.endsWith(item.route)) {
          if (newPath.length > bestMatch.length) {
            bestMatch = newPath;
          }
        }

        if (item.children) {
          findPath(item.children, newPath, url);
        }
      }
    };

    findPath(this.navigationItems, [], this.activeUrl);
    return bestMatch;
  }

  /**
   * Updates `isActive` flags on nav items based on the current active route.
   * Does NOT mutate `isExpanded` — that's the user's job (or a navigation event).
   */
  private updateActiveState(): void {
    const resetActive = (items: NavItem[]) => {
      for (const item of items) {
        item.isActive = false;
        if (item.children) {
          resetActive(item.children);
        }
      }
    };
    resetActive(this.navigationItems);

    const bestMatch = this.findActiveBestMatch();
    if (bestMatch.length === 0) {
      return;
    }

    if (this.sidebarState !== 'collapsed') {
      // When expanded or peeking, only the exact leaf is active
      bestMatch[bestMatch.length - 1].isActive = true;
    } else {
      // When collapsed, highlight the top-level parent of the match
      bestMatch[0].isActive = true;
    }
  }

  /**
   * Auto-expands the top-level parent of the currently-active route, with
   * mutual exclusion (other parents collapse). Called only on init and on
   * actual route navigation — NOT on every state transition, otherwise
   * peeking at a different parent would fight against the user's intent.
   */
  private syncExpandedToActiveRoute(): void {
    if (this.sidebarState === 'collapsed') {
      return;
    }
    const bestMatch = this.findActiveBestMatch();
    if (bestMatch.length < 2) {
      return; // route is a top-level leaf with no parent to expand
    }
    this.collapseAllTopLevelItems(bestMatch[0]);
    bestMatch[0].isExpanded = true;
  }

  /**
   * Recursively collapses all expandable navigation items.
   */
  private collapseAllItems(items: NavItem[]): void {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        item.isExpanded = false;
        this.collapseAllItems(item.children); // Recursive call
      }
    }
  }

  /**
   * Collapses all top-level accordion items except the specified one.
   */
  private collapseAllTopLevelItems(exceptItem?: NavItem): void {
    for (const item of this.navigationItems) {
      if (item.children && item !== exceptItem) {
        item.isExpanded = false;
        this.collapseAllItems(item.children);
      }
    }
  }
}
